import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useBrand } from "../../context/BrandContext";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import {
  formatPlayerStats,
  playerDisplayName,
} from "../../utils/liveTrainingPlayerContext";
import { CoachTablePreviewCard } from "./CoachTablePreviewCard";
import "./CoachTablePreviewCard.css";
import "./LiveTrainingTableSetupWizard.css";
import "./LiveTrainingTableSwapDialog.css";
import "./LiveTrainingTableMoveDialog.css";

const TABLES_PER_PAGE = 5;

function playerIdOf(player) {
  if (!player) return null;
  const id = player.player_id ?? player.playerId ?? null;
  return id != null ? String(id) : null;
}

function tableOrderKey(table) {
  return String(table?.tableId ?? table?.tableNumber ?? "");
}

function padPageSlots(pageItems, pageSize) {
  return Array.from({ length: pageSize }, (_, i) => pageItems[i] ?? null);
}

function chunkTables(list, size) {
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages.length ? pages : [[]];
}

/**
 * Force-move one or more present players onto any table (can exceed 2 seats).
 * Flow: tap players to select (multi), then tap a destination table.
 */
export function LiveTrainingTableMoveDialog({
  open,
  onClose,
  tables = [],
  roster = [],
  /** Present players seated on another station (movable onto this station). */
  otherStationPlayers = [],
  /** Map of tsg_id → display label for other-station chips. */
  stationLabelsById = null,
  stations = null,
  focusedTsgId = null,
  teId = null,
  tableSetup,
  exerciseLocked = false,
  roomCapacity = 0,
  usedTableCount = 0,
  onSelectionChange = null,
  initialSelectedPlayers = null,
  /** Stack above session/exercise results overlays. */
  elevated = false,
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandSecondary = brand?.color_secondary || "#46528b";
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [orderKeys, setOrderKeys] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [boardTsgId, setBoardTsgId] = useState(null);
  const [foreignTables, setForeignTables] = useState([]);
  const [foreignLoading, setForeignLoading] = useState(false);
  const pagesRef = useRef(null);
  const initialSelectedRef = useRef(initialSelectedPlayers);
  initialSelectedRef.current = initialSelectedPlayers;

  const multiStation = Array.isArray(stations) && stations.length > 1;
  const viewingOther =
    multiStation &&
    boardTsgId != null &&
    focusedTsgId != null &&
    String(boardTsgId) !== String(focusedTsgId);
  const displayTables = viewingOther
    ? foreignTables
    : tableSetup?.tables ?? tables;

  useEffect(() => {
    if (!open) {
      setBoardTsgId(focusedTsgId != null ? String(focusedTsgId) : null);
      setForeignTables([]);
      setForeignLoading(false);
      return;
    }
    setBoardTsgId(focusedTsgId != null ? String(focusedTsgId) : null);
  }, [open, focusedTsgId]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !viewingOther || !boardTsgId) {
      setForeignTables([]);
      setForeignLoading(false);
      return undefined;
    }
    setForeignTables([]);
    setForeignLoading(true);
    void (async () => {
      const rows = (await tableSetup?.loadStationTables?.(boardTsgId)) || [];
      if (cancelled) return;
      setForeignTables(rows);
      setForeignLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, viewingOther, boardTsgId, tableSetup?.loadStationTables]);

  useEffect(() => {
    if (!open) {
      setSelectedPlayers([]);
      setBusy(false);
      setStatusMsg(null);
      setOrderKeys([]);
      setPageIndex(0);
      return;
    }
    const seeded = Array.isArray(initialSelectedRef.current)
      ? initialSelectedRef.current
          .map((p) => {
            const id = p?.playerId ?? p?.player_id ?? null;
            if (id == null) return null;
            return {
              playerId: String(id),
              name:
                p?.name ||
                playerDisplayName(p) ||
                t("player") ||
                "Player",
              fromTableNumber: p?.fromTableNumber ?? null,
              fromTableId: p?.fromTableId ?? null,
              fromTeId: p?.fromTeId ?? null,
              fromTsgId: p?.fromTsgId ?? null,
              fromSlot: p?.fromSlot ?? null,
            };
          })
          .filter(Boolean)
      : [];
    setSelectedPlayers(seeded);
    setBusy(false);
    setStatusMsg(null);
    setPageIndex(0);
  }, [open, t]);

  useEffect(() => {
    if (typeof onSelectionChange === "function") {
      onSelectionChange(selectedPlayers);
    }
  }, [selectedPlayers, onSelectionChange]);

  useEffect(() => {
    if (!open) return;
    const sorted = [...(displayTables || [])].sort(
      (a, b) => (a.tableNumber || 0) - (b.tableNumber || 0)
    );
    const keys = sorted.map(tableOrderKey).filter(Boolean);
    setOrderKeys((prev) => {
      if (prev.length === 0) return keys;
      const keySet = new Set(keys);
      const next = prev.filter((k) => keySet.has(k));
      keys.forEach((k) => {
        if (!next.includes(k)) next.push(k);
      });
      return next;
    });
  }, [open, displayTables]);

  const setupTables = useMemo(() => {
    const list = Array.isArray(displayTables) ? displayTables : [];
    const byKey = new Map(list.map((table) => [tableOrderKey(table), table]));
    let ordered;
    if (orderKeys.length === 0) {
      ordered = [...list].sort(
        (a, b) => (a.tableNumber || 0) - (b.tableNumber || 0)
      );
    } else {
      ordered = [];
      orderKeys.forEach((key) => {
        const table = byKey.get(key);
        if (table) ordered.push(table);
      });
      list.forEach((table) => {
        const key = tableOrderKey(table);
        if (!orderKeys.includes(key)) ordered.push(table);
      });
    }
    const stationId = boardTsgId || focusedTsgId;
    return ordered.map((table) => ({
      ...table,
      teId: table.teId || (viewingOther ? null : teId),
      tsgId: table.tsgId || stationId || null,
    }));
  }, [boardTsgId, displayTables, focusedTsgId, orderKeys, teId, viewingOther]);

  const pages = useMemo(
    () => chunkTables(setupTables, TABLES_PER_PAGE),
    [setupTables]
  );
  const pageCount = pages.length;
  const safePageIndex = Math.min(pageIndex, Math.max(0, pageCount - 1));

  const goToPage = useCallback(
    (next) => {
      const clamped = Math.max(0, Math.min(pageCount - 1, next));
      setPageIndex(clamped);
      const scroller = pagesRef.current;
      if (!scroller) return;
      const pageWidth = scroller.clientWidth || 1;
      scroller.scrollTo({ left: clamped * pageWidth, behavior: "smooth" });
    },
    [pageCount]
  );

  useEffect(() => {
    const scroller = pagesRef.current;
    if (!scroller || !open) return undefined;
    const onScroll = () => {
      const pageWidth = scroller.clientWidth || 1;
      const next = Math.round(scroller.scrollLeft / pageWidth);
      setPageIndex((prev) => (prev === next ? prev : next));
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [open, pageCount]);

  useEffect(() => {
    const scroller = pagesRef.current;
    if (!scroller || !open) return;
    const pageWidth = scroller.clientWidth || 1;
    scroller.scrollTo({ left: safePageIndex * pageWidth, behavior: "auto" });
  }, [open, safePageIndex]);

  const assignedIds = useMemo(() => {
    const ids = new Set();
    for (const table of setupTables) {
      for (const key of ["player1", "player2", "player3"]) {
        const id = playerIdOf(table?.[key]);
        if (id) ids.add(id);
      }
      for (const extra of table?.extras || []) {
        const id = playerIdOf(extra);
        if (id) ids.add(id);
      }
    }
    if (tableSetup?.assignedIds instanceof Set) {
      for (const id of tableSetup.assignedIds) ids.add(String(id));
    }
    return ids;
  }, [setupTables, tableSetup?.assignedIds]);

  const otherStationList = useMemo(
    () => (Array.isArray(otherStationPlayers) ? otherStationPlayers : []),
    [otherStationPlayers]
  );

  const otherStationIds = useMemo(() => {
    const ids = new Set();
    for (const p of otherStationList) {
      const id = playerIdOf(p);
      if (id) ids.add(id);
    }
    return ids;
  }, [otherStationList]);

  const unassigned = useMemo(
    () =>
      (roster || []).filter((p) => {
        const id = playerIdOf(p);
        return id && !assignedIds.has(id) && !otherStationIds.has(id);
      }),
    [roster, assignedIds, otherStationIds]
  );

  const selectedIds = useMemo(
    () => new Set(selectedPlayers.map((p) => p.playerId)),
    [selectedPlayers]
  );

  const stationLabelFor = useCallback(
    (tsgId) => {
      if (tsgId == null || tsgId === "") return null;
      const key = String(tsgId);
      if (stationLabelsById && typeof stationLabelsById === "object") {
        return stationLabelsById[key] || stationLabelsById[tsgId] || key;
      }
      return key;
    },
    [stationLabelsById]
  );

  const pickPlayer = useCallback(
    (player, fromTableNumber = null, fromSlot = null, meta = null) => {
      if (exerciseLocked || busy) return;
      const id = playerIdOf(player);
      if (!id) return;
      setStatusMsg(null);
      setSelectedPlayers((prev) => {
        if (prev.some((p) => p.playerId === id)) {
          return prev.filter((p) => p.playerId !== id);
        }
        return [
          ...prev,
          {
            playerId: id,
            name:
              playerDisplayName(player) ||
              player?.name ||
              t("player") ||
              "Player",
            fromTableNumber,
            fromSlot,
            fromTableId:
              meta?.fromTableId ?? player?.seated_table_id ?? null,
            fromTeId: meta?.fromTeId ?? player?.seated_te_id ?? null,
            fromTsgId: meta?.fromTsgId ?? player?.seated_tsg_id ?? null,
          },
        ];
      });
    },
    [busy, exerciseLocked, t]
  );

  const pickTable = useCallback(
    async (table) => {
      if (exerciseLocked || busy || selectedPlayers.length === 0) return;
      const tn = Number(table?.tableNumber);
      if (!Number.isFinite(tn) || tn <= 0) return;

      const tableId = table?.tableId ?? table?.raw?.table_id ?? null;
      const destTsg = table?.tsgId || boardTsgId || focusedTsgId;
      const movers = selectedPlayers.filter((src) => {
        if (
          tableId != null &&
          src.fromTableId != null &&
          String(src.fromTableId) === String(tableId)
        ) {
          return false;
        }
        if (src.fromTableId != null) return true;
        return (
          src.fromTableNumber == null || Number(src.fromTableNumber) !== tn
        );
      });
      if (movers.length === 0) {
        setSelectedPlayers([]);
        return;
      }

      const destIsOther =
        viewingOther ||
        (destTsg &&
          focusedTsgId &&
          String(destTsg) !== String(focusedTsgId)) ||
        (table?.teId && teId && String(table.teId) !== String(teId));

      setBusy(true);
      setStatusMsg(null);
      try {
        const result = destIsOther
          ? await tableSetup?.movePlayersToStation?.(
              movers.map((m) => m.playerId),
              destTsg,
              { tableNumber: tn, tableId }
            )
          : await (
              tableSetup?.movePlayersForce ||
              (async (ids, tableNumber) => {
                if (
                  typeof tableSetup?.movePlayerForce === "function" &&
                  ids.length === 1
                ) {
                  return tableSetup.movePlayerForce(ids[0], tableNumber);
                }
                return { ok: false, reason: "missing_move" };
              })
            )(movers.map((m) => m.playerId), { tableNumber: tn, tableId });
        if (result?.ok) {
          setStatusMsg(
            t("liveTrainingTableMoveMultiDone", {
              names: movers.map((m) => m.name).join(", "),
              table: tn,
              defaultValue: `${movers.map((m) => m.name).join(", ")} → table ${tn}`,
            })
          );
          setSelectedPlayers([]);
        } else {
          setStatusMsg(
            tableSetup?.error ||
              result?.message ||
              t("liveTrainingTableMoveError") ||
              "Could not move player."
          );
        }
      } finally {
        setBusy(false);
      }
    },
    [boardTsgId, busy, exerciseLocked, focusedTsgId, selectedPlayers, t, tableSetup, teId, viewingOther]
  );

  const portalRoot = getLiveTrainingPortalRoot();
  if (!open || !portalRoot) return null;

  const otherStationVisible = otherStationList.filter((p) => {
    if (!viewingOther || boardTsgId == null) return true;
    return String(p?.seated_tsg_id || "") !== String(boardTsgId);
  });
  const tableCapacity = roomCapacity || setupTables.length || 0;
  const pickingTable = selectedPlayers.length > 0;
  const selectedLabel = selectedPlayers.map((p) => p.name).join(", ");
  const statusMessage = statusMsg
    ? statusMsg
    : pickingTable
      ? t("liveTrainingTableMoveSourceSelected", {
          name: selectedLabel,
        }) || `Selected: ${selectedLabel} — tap a table`
      : multiStation
        ? t("liveTrainingTableMoveAcrossHint") ||
          "Pick a station, tap players, then tap a table — including tables on another station."
        : t("liveTrainingTableMoveMultiHint") ||
          "Tap one or more players, then tap a destination table.";

  return createPortal(
    <div
      className={`lt-table-wizard__backdrop lt-table-swap-dialog__backdrop lt-table-move-dialog__backdrop${
        elevated ? " lt-table-swap-dialog__backdrop--elevated" : ""
      }`}
      role="presentation"
    >
      <div
        className="lt-table-wizard lt-table-wizard--fullscreen lt-table-swap-dialog lt-table-move-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-table-move-title"
        style={{
          "--lt-brand": brandPrimary,
          "--lt-brand-secondary": brandSecondary,
        }}
      >
        <header className="lt-table-wizard__header">
          <div>
            <h2 id="lt-table-move-title">
              <OpenWithIcon fontSize="small" aria-hidden />
              {t("liveTrainingTableMoveTitle") || "Move player"}
            </h2>
            <p className="lt-table-wizard__subtitle">
              {usedTableCount}/{tableCapacity} {t("tables") || "tables"}
            </p>
          </div>
          <button
            type="button"
            className="lt-table-wizard__close"
            onClick={onClose}
            aria-label={t("close")}
          >
            <CloseIcon />
          </button>
        </header>

        {multiStation ? (
          <div
            className="lt-table-swap-dialog__stations"
            role="tablist"
            aria-label={t("liveTrainingTableMoveToStation") || "Station"}
          >
            {stations.map((station) => {
              const id = String(station.tsg_id);
              const selected = String(boardTsgId || focusedTsgId || "") === id;
              const label =
                (stationLabelsById && stationLabelsById[id]) ||
                station.name ||
                id;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`lt-table-swap-dialog__station-btn${
                    selected ? " is-selected" : ""
                  }`}
                  disabled={exerciseLocked || busy}
                  onClick={() => {
                    setBoardTsgId(id);
                    setPageIndex(0);
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="lt-table-wizard__body lt-table-swap-dialog__body">
          {(tableSetup?.loading || foreignLoading) && setupTables.length === 0 ? (
            <p className="lt-table-wizard__loading">{t("loading") || "Loading…"}</p>
          ) : setupTables.length === 0 ? (
            <p className="lt-table-wizard__empty-msg">
              {t("noTablesConfigured") || "No tables configured."}
            </p>
          ) : (
            <div className="lt-table-swap-dialog__pager">
              {pageCount > 1 ? (
                <button
                  type="button"
                  className="lt-table-swap-dialog__edge lt-table-swap-dialog__edge--prev"
                  onClick={() => goToPage(safePageIndex - 1)}
                  disabled={safePageIndex <= 0}
                  aria-label={t("previous", "Previous")}
                >
                  <ChevronLeftIcon />
                </button>
              ) : null}
              <div
                className="lt-table-swap-dialog__pages"
                ref={pagesRef}
                role="region"
                aria-label={t("tables") || "Tables"}
              >
                {pages.map((pageItems, pageIdx) => {
                  const slots = padPageSlots(pageItems, TABLES_PER_PAGE);
                  return (
                    <div
                      key={`move-page-${pageIdx}`}
                      className="lt-table-swap-dialog__page"
                    >
                      <div className="lt-table-swap-dialog__tables-grid">
                        {slots.map((table, slotIdx) => {
                          if (!table) {
                            return (
                              <div
                                key={`empty-${pageIdx}-${slotIdx}`}
                                className="lt-coach-table lt-coach-table--slot-empty lt-table-swap-dialog__card"
                                aria-hidden
                              />
                            );
                          }

                          const p1Id = playerIdOf(table.player1);
                          const p2Id = playerIdOf(table.player2);
                          const p3Id = playerIdOf(table.player3);
                          const p1Selected = p1Id && selectedIds.has(p1Id);
                          const p2Selected = p2Id && selectedIds.has(p2Id);
                          const p3Selected = p3Id && selectedIds.has(p3Id);
                          const extraSelected = (table.extras || []).some((p) =>
                            selectedIds.has(playerIdOf(p))
                          );
                          const isSourceTable =
                            p1Selected || p2Selected || p3Selected || extraSelected;
                          const selectedSlots = [
                            p1Selected ? "player1" : null,
                            p2Selected ? "player2" : null,
                            p3Selected ? "player3" : null,
                            ...(table.extras || []).map((p, i) =>
                              selectedIds.has(playerIdOf(p)) ? `extra:${i}` : null
                            ),
                          ].filter(Boolean);
                          const selectedSlot =
                            selectedSlots.length > 0 ? selectedSlots : null;
                          const isTargetTable = pickingTable && !isSourceTable;
                          const seatMeta = {
                            fromTableId: table.tableId ?? null,
                            fromTeId: table.teId || teId,
                            fromTsgId: table.tsgId || boardTsgId || focusedTsgId,
                          };

                          return (
                            <CoachTablePreviewCard
                              key={tableOrderKey(table) || table.tableNumber}
                              tableNumber={table.tableNumber}
                              player1={table.player1}
                              player2={table.player2}
                              player3={table.player3}
                              extras={table.extras}
                              t={t}
                              swapMode={!isTargetTable}
                              hideSwapBadges
                              disabled={exerciseLocked || busy}
                              selected={isSourceTable}
                              selectedSlot={selectedSlot}
                              onSelectPlayer1={() =>
                                pickPlayer(
                                  table.player1,
                                  table.tableNumber,
                                  "player1",
                                  seatMeta
                                )
                              }
                              onSelectPlayer2={() =>
                                pickPlayer(
                                  table.player2,
                                  table.tableNumber,
                                  "player2",
                                  seatMeta
                                )
                              }
                              onSelectPlayer3={() =>
                                pickPlayer(
                                  table.player3,
                                  table.tableNumber,
                                  "player3",
                                  seatMeta
                                )
                              }
                              onSelectExtra={(player, index) =>
                                pickPlayer(
                                  player,
                                  table.tableNumber,
                                  `extra:${index}`,
                                  seatMeta
                                )
                              }
                              onTableClick={
                                isTargetTable
                                  ? () => pickTable(table)
                                  : undefined
                              }
                              className={[
                                "lt-table-wizard__card",
                                "lt-table-swap-dialog__card",
                                isSourceTable
                                  ? "lt-table-swap-dialog__card--source-table"
                                  : isTargetTable
                                    ? "lt-table-swap-dialog__card--target-table"
                                    : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {pageCount > 1 ? (
                <button
                  type="button"
                  className="lt-table-swap-dialog__edge lt-table-swap-dialog__edge--next"
                  onClick={() => goToPage(safePageIndex + 1)}
                  disabled={safePageIndex >= pageCount - 1}
                  aria-label={t("next", "Next")}
                >
                  <ChevronRightIcon />
                </button>
              ) : null}
            </div>
          )}

          {otherStationVisible.length > 0 ? (
            <section className="lt-table-move-dialog__unassigned lt-table-move-dialog__other-station">
              <h3>
                {t("liveTrainingTableMoveOtherStation") ||
                  "Players on other stations"}
              </h3>
              <p className="lt-table-move-dialog__other-station-hint">
                {t("liveTrainingTableMoveOtherStationHint") ||
                  "Tap a player, then a table here to move them to this station."}
              </p>
              <ul>
                {otherStationVisible.map((p) => {
                  const id = playerIdOf(p);
                  const fromLabel = stationLabelFor(p.seated_tsg_id);
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        className={`lt-table-move-dialog__unassigned-btn${
                          selectedIds.has(id)
                            ? " lt-table-move-dialog__unassigned-btn--selected"
                            : ""
                        }`}
                        disabled={exerciseLocked || busy}
                        onClick={() => pickPlayer(p, null, null)}
                        aria-pressed={selectedIds.has(id)}
                      >
                        <span className="lt-table-move-dialog__other-station-name">
                          <span>{playerDisplayName(p)}</span>
                          {fromLabel ? (
                            <span className="lt-table-move-dialog__station-chip">
                              {fromLabel}
                            </span>
                          ) : null}
                        </span>
                        <span>
                          {formatPlayerStats(p.classement, p.ranking_points, t) ||
                            p.ranking_points ||
                            "—"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {unassigned.length > 0 ? (
            <section className="lt-table-move-dialog__unassigned">
              <h3>
                {t("liveTrainingTableWizardUnassigned") || "Unassigned players"}
              </h3>
              <ul>
                {unassigned.map((p) => {
                  const id = playerIdOf(p);
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        className={`lt-table-move-dialog__unassigned-btn${
                          selectedIds.has(id)
                            ? " lt-table-move-dialog__unassigned-btn--selected"
                            : ""
                        }`}
                        disabled={exerciseLocked || busy}
                        onClick={() => pickPlayer(p, null, null)}
                        aria-pressed={selectedIds.has(id)}
                      >
                        <span>{playerDisplayName(p)}</span>
                        <span>
                          {formatPlayerStats(p.classement, p.ranking_points, t) ||
                            p.ranking_points ||
                            "—"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </div>

        <div
          className={`lt-table-swap-dialog__status${
            pickingTable || statusMsg
              ? " lt-table-swap-dialog__status--picking"
              : ""
          }`}
          role="status"
        >
          <span className="lt-table-swap-dialog__status-text">
            {pickingTable ? (
              <OpenWithIcon fontSize="small" aria-hidden />
            ) : null}
            {statusMessage}
          </span>
          {pickingTable ? (
            <button
              type="button"
              className="lt-table-swap-dialog__source-cancel"
              onClick={() => setSelectedPlayers([])}
              disabled={busy}
            >
              <CloseIcon fontSize="small" aria-hidden />
              {t("cancel")}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    portalRoot
  );
}

export default LiveTrainingTableMoveDialog;
